import React, { useEffect, useState, useCallback } from "react"
import { Card, CardBody, Button } from "reactstrap"
import { useNavigate, useLocation } from "react-router-dom"

import { toast } from "react-toastify"

import GradeHeader from "../components/GradeHeader"
import GradeGrid from "../components/GradeGrid"
import { getStartOfWeek, addDays, toISODate } from "../../../utils/date"

import NeoLoader from "../../../components/Common/NeoLoader"

import SessionService from "../../../services/session_service"
import EnrollmentService from "../../../services/enrollment"
import WaitlistService from "../../../services/waitlist_service"
import ConfirmDialog from "../../../components/Common/ConfirmDialog"

import { doc, getDoc } from "firebase/firestore"
import { db } from "../../../services/firebase"
import { COLLECTIONS } from "../../../services/collections"

import { useTenant } from "../../../hooks/useTenant"

import "./EnrollmentGrade.scss"

/**
 * Página de Seleção de Turma/Sessão para Matrícula ou Aula Experimental.
 * Consome o GradeContext para manter a data e filtros sincronizados com a Grade Principal.
 */
const EnrollmentGrade = ({
    isEmbed = false,
    embedMode = 'regular',
    onConfirmEmbed = null,
    onCancelEmbed = null,
    preSelectedClasses = [],
    contractLimits = null
}) => {

    const navigate = useNavigate()
    const location = useLocation()
    const { idTenant, user } = useTenant()

    // 1. Estados Locais de Grade (Substituindo GradeContext)
    const [sessions, setSessions] = useState([])
    const [sessionsCache, setSessionsCache] = useState({})
    const [loadingData, setLoadingData] = useState(false)
    const [referenceDate, setReferenceDate] = useState(new Date())
    const [view, setView] = useState('week')
    const [turn, setTurn] = useState('all')

    // String memoizada para evitar o loop infinito no useCallback e useEffect
    // pois objetos Date seriam recriados e falhariam a equality de dependência
    const weekStartISO = view === 'week' ? toISODate(getStartOfWeek(referenceDate)) : toISODate(referenceDate);
    const weekEndISO = view === 'week' ? toISODate(addDays(getStartOfWeek(referenceDate), 6)) : toISODate(referenceDate);

    const fetchSessions = useCallback(async (forceRefresh = false) => {
        if (!idTenant) return;

        const cacheKey = `${weekStartISO}_${weekEndISO}_${view}`;
        if (!forceRefresh && sessionsCache[cacheKey]) {
            let filtered = sessionsCache[cacheKey];
            if (turn !== 'all') {
                const isMorning = turn === 'morning';
                const isAfternoon = turn === 'afternoon';
                const isEvening = turn === 'evening';
                filtered = filtered.filter(s => {
                    const hour = parseInt((s.startTime || "00:00").split(":")[0], 10);
                    if (isMorning) return hour < 12;
                    if (isAfternoon) return hour >= 12 && hour < 18;
                    if (isEvening) return hour >= 18;
                    return true;
                });
            }
            setSessions(filtered);
            return;
        }

        setLoadingData(true);
        try {
            const data = await SessionService.getSessions(idTenant, weekStartISO, weekEndISO);

            let filtered = data;
            if (turn !== 'all') {
                const isMorning = turn === 'morning';
                const isAfternoon = turn === 'afternoon';
                const isEvening = turn === 'evening';
                filtered = data.filter(s => {
                    const hour = parseInt((s.startTime || "00:00").split(":")[0], 10);
                    if (isMorning) return hour < 12;
                    if (isAfternoon) return hour >= 12 && hour < 18;
                    if (isEvening) return hour >= 18;
                    return true;
                });
            }
            setSessions(filtered);
            setSessionsCache(prev => ({ ...prev, [cacheKey]: data }));
        } catch (error) {
            console.error("Erro ao buscar sessoes:", error);
        } finally {
            setLoadingData(false);
        }
    }, [idTenant, weekStartISO, weekEndISO, turn, view, sessionsCache]);

    const refresh = () => fetchSessions(true);

    useEffect(() => {
        if (idTenant) {
            fetchSessions();
        }
    }, [idTenant, fetchSessions]);

    // 2. Parâmetros da URL e Resolução de Contexto
    const searchParams = new URLSearchParams(location.search)
    const idMember = searchParams.get('idMember') || '' // PDV não tem idMember real até fechar a venda
    const memberName = searchParams.get('memberName') || ''
    const mode = isEmbed ? embedMode : searchParams.get('mode') // 'regular' ou 'trial'
    const idMembership = searchParams.get('idMembership') // Só via URL

    // 3. Estados Locais de Seleção
    const [selectedClasses, setSelectedClasses] = useState(preSelectedClasses || []) // Para mode=regular
    const [selectedSession, setSelectedSession] = useState(null) // Para mode=trial
    const [enrolling, setEnrolling] = useState(false)
    const [existingEnrollments, setExistingEnrollments] = useState([]) // Matrículas já existentes
    const [fetchedContractLimits, setFetchedContractLimits] = useState(null) // Limites carregados do BD

    // Buscar matrículas existentes do cliente para marcar na grade
    // E buscar regras do contrato ativo para validação de seleção
    useEffect(() => {
        const fetchMemberContext = async () => {
            if (isEmbed) return // Modo embed recebe contractLimits via prop
            if (!idMember || !idTenant) return

            try {
                // Matrículas existentes
                const enrollments = await EnrollmentService.listMemberEnrollments(idTenant, idMember)
                const activeEnrollments = enrollments.filter(e => e.status === 'active')
                setExistingEnrollments(activeEnrollments)

                // Buscar contrato ativo do membro para aplicar regras de acesso
                const { default: MemberMembershipService } = await import('../../../services/member_membership_service')
                const memberMemberships = await MemberMembershipService.getActiveByMember(idTenant, idMember)
                if (memberMemberships.length > 0) {
                    // Usa o contrato mais recente (último ativo)
                    const activeMembership = memberMemberships[memberMemberships.length - 1]
                    setFetchedContractLimits(activeMembership)
                }
            } catch (error) {
                console.error('Erro ao buscar contexto do membro:', error)
            }
        }

        fetchMemberContext()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idMember, idTenant])

    // Resolve as regras de contrato: prop (embed/PDV) ou buscado do BD (perfil do membro)
    const resolvedContractLimits = contractLimits || fetchedContractLimits



    const handleSelectSchedule = (schedule) => {
        if (isClassEnrolled(schedule)) {
            toast.warning('Este aluno já está matriculado permanentemente nesta turma!');
            return;
        }

        if (mode === 'trial') {
            // Modo experimental: seleciona apenas UMA sessão
            setSelectedSession(schedule.id === selectedSession ? null : schedule.id)
        } else {
            const isPackage = resolvedContractLimits && resolvedContractLimits.durationType === 'classes';
            const sessionId = schedule.id;
            const classId = schedule.idClass;
            const sessionDate = schedule.sessionDate || schedule.dateString || toISODate(referenceDate);

            if (isPackage) {
                // Modo Pacote de Aulas: seleciona SESSÕES avulsas
                if (selectedClasses.some(c => c.sessionId === sessionId)) {
                    setSelectedClasses(prev => prev.filter(c => c.sessionId !== sessionId));
                } else {
                    const remaining = resolvedContractLimits.remainingClasses ?? 0;
                    if (selectedClasses.length >= remaining) {
                        toast.error(`Você só tem mais ${remaining} aula(s) restante(s) no pacote.`);
                        return;
                    }
                    setSelectedClasses(prev => [...prev, {
                        sessionId,
                        idClass: classId,
                        startDate: sessionDate,
                        activityName: schedule.activityName || schedule.className || 'Atividade',
                        teacherName: schedule.employeeName || schedule.teacherName || 'Professor',
                        startTime: schedule.startTime || schedule.time || '--:--',
                        dayOfWeek: schedule.weekDayName || schedule.dayOfWeekStr || 'Dia'
                    }]);
                }
            } else {
                // Modo regular: seleciona a TURMA (idClass)
                if (selectedClasses.some(c => c.idClass === classId)) {
                    setSelectedClasses(prev => prev.filter(c => c.idClass !== classId))
                } else {
                    // Validação de Contrato em Tempo Real
                    if (resolvedContractLimits) {
                        const maxSessions = resolvedContractLimits.maxSessionsPerWeek || 0;
                        const allowedDays = resolvedContractLimits.allowedDaysOfWeek || [];
                        
                        // Total = matrículas já ativas no banco + seleções desta sessão
                        const totalEnrollments = existingEnrollments.length + selectedClasses.length;
                        if (maxSessions > 0 && totalEnrollments >= maxSessions) {
                            toast.error(`Limite excedido: O plano permite ${maxSessions} turma(s). Já possui ${existingEnrollments.length} matrícula(s) ativa(s).`);
                            return;
                        }
                        
                        if (allowedDays.length > 0) {
                            if (schedule.weekday !== undefined && schedule.weekday !== null && !allowedDays.includes(schedule.weekday)) {
                                const DAYS = ["Domingos", "Segundas", "Terças", "Quartas", "Quintas", "Sextas", "Sábados"];
                                toast.error(`Acesso negado: O plano não abrange ${DAYS[schedule.weekday]}.`);
                                return;
                            }
                        }
                    }

                    setSelectedClasses(prev => [...prev, {
                        idClass: classId,
                        startDate: sessionDate,
                        activityName: schedule.activityName || schedule.className || 'Atividade',
                        teacherName: schedule.employeeName || schedule.teacherName || 'Professor',
                        startTime: schedule.startTime || schedule.time || '--:--',
                        dayOfWeek: schedule.weekDayName || schedule.dayOfWeekStr || 'Dia'
                    }]);
                }
            }
        }
    }

    // Confirmar matrícula/experimental
    const handleConfirm = async () => {
        // Modo Embed (Passando o payload para a tela superior sem salvar no BD)
        if (isEmbed) {
            if (mode === 'trial' && !selectedSession) {
                toast.error('Selecione uma sessão'); return;
            }
            if (mode === 'regular' && selectedClasses.length === 0) {
                toast.error('Selecione ao menos uma turma'); return;
            }
            if (onConfirmEmbed) {
                onConfirmEmbed(mode === 'trial' ? [selectedSession] : selectedClasses);
            }
            return;
        }

        if (!idMember) {
            toast.error('Membro não identificado')
            return
        }

        if (mode === 'trial' && !selectedSession) {
            toast.error('Selecione uma sessão para a aula experimental')
            return
        }

        if (mode === 'regular' && selectedClasses.length === 0) {
            toast.error('Selecione ao menos uma turma para matrícula')
            return
        }

        try {
            setEnrolling(true)

            if (mode === 'trial') {
                // Agendar experimental
                await EnrollmentService.scheduleTrialClass(idTenant, user, {
                    idMember,
                    sessionId: selectedSession,
                    memberName
                })

                // Automação feita via Firestore Trigger (onTrialScheduled)

                toast.success('Aula experimental agendada com sucesso!')
            } else {
                const isPackage = resolvedContractLimits && resolvedContractLimits.durationType === 'classes';
                
                if (isPackage) {
                    // Matrícula avulsa de pacote de aulas
                    const targetMembershipId = idMembership || (resolvedContractLimits ? resolvedContractLimits.id : null);
                    
                    await EnrollmentService.enrollClassPackage(idTenant, user, {
                        idMember,
                        idMembership: targetMembershipId,
                        selectedSessions: selectedClasses,
                        memberName: memberName || 'Cliente'
                    });
                    toast.success(`Pacote de aulas: ${selectedClasses.length} sessão(ões) confirmada(s)!`);
                } else {
                    // Matrícula regular em múltiplas turmas
                    const targetMembershipId = idMembership || (resolvedContractLimits ? resolvedContractLimits.id : null);

                    await EnrollmentService.enrollMember(idTenant, user, {
                        idMember,
                        idMembership: targetMembershipId,
                        selectedClasses, // Array de { idClass, startDate }
                        memberName: memberName || 'Cliente'
                    });
                    toast.success(`${memberName || 'Cliente'} matriculado(a) com sucesso!`);
                }
            }

            // Atualizar cache da grade forçando atualização antes de sair
            await fetchSessions(true)

            // Voltar para a página anterior (perfil do membro)
            navigate(-1)

        } catch (error) {
            console.error('Erro ao confirmar:', error)

            // Interceptar erro de turma lotada → oferecer lista de espera
            if (error.message?.startsWith('TURMA_LOTADA:')) {
                const [, classId, className] = error.message.split(':')

                const isConfirmed = await ConfirmDialog({
                    title: 'Turma Lotada!',
                    text: `A turma "${className}" atingiu a capacidade máxima. Deseja adicionar ${memberName || 'o aluno'} à Lista de Espera?`,
                    confirmButtonText: 'Sim, adicionar à lista',
                    cancelButtonText: 'Não, voltar',
                    confirmButtonColor: '#f0ad4e',
                    icon: 'warning',
                })

                if (isConfirmed) {
                    try {
                        // Buscar dados completos da turma e do membro para desnormalizar
                        const classRef = doc(db, 'tenants', idTenant, COLLECTIONS.CLASSES, classId)
                        const classSnap = await getDoc(classRef)
                        const classData = classSnap.exists() ? classSnap.data() : {}

                        let memberData = {}
                        if (idMember) {
                            const memberRef = doc(db, 'tenants', idTenant, COLLECTIONS.MEMBERS, idMember)
                            const memberSnap = await getDoc(memberRef)
                            memberData = memberSnap.exists() ? memberSnap.data() : {}
                        }

                        const result = await WaitlistService.addToWaitlist(idTenant, user, {
                            idMember,
                            memberName: memberName || memberData.fullName || 'Cliente',
                            memberPhone: memberData.phone || '',
                            memberEmail: memberData.email || '',
                            memberPhotoUrl: memberData.photoUrl || '',
                            idClass: classId,
                            classData: {
                                activityName: classData.activityName || className || '',
                                weekdayLabel: classData.weekdayLabel || '',
                                startTime: classData.startTime || '',
                                endTime: classData.endTime || '',
                                employeeName: classData.employeeName || '',
                            },
                        })

                        toast.success(`${memberName || 'Aluno'} adicionado(a) à lista de espera (posição #${result.position})!`)
                        navigate(-1)
                    } catch (waitlistError) {
                        console.error('Erro ao adicionar à lista de espera:', waitlistError)
                        toast.error(waitlistError.message || 'Erro ao adicionar à lista de espera.')
                    }
                }
            } else {
                toast.error(error.message || 'Erro ao processar solicitação')
            }
        } finally {
            setEnrolling(false)
        }
    }

    const handleCancel = () => {
        if (isEmbed && onCancelEmbed) {
            onCancelEmbed();
        } else {
            navigate(-1);
        }
    }

    const isClassEnrolled = (schedule) => {
        return existingEnrollments.some(e => e.idClass === schedule.idClass)
    }

    const isClassSelected = (schedule) => {
        if (mode === 'trial') {
            return schedule.id === selectedSession;
        } else {
            const isPackage = resolvedContractLimits && resolvedContractLimits.durationType === 'classes';
            if (isPackage) {
                return selectedClasses.some(c => c.sessionId === schedule.id);
            } else {
                return selectedClasses.some(c => c.idClass === schedule.idClass);
            }
        }
    }

    if (loadingData && sessions.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <NeoLoader type="inline" loading={loadingData} />
            </div>
        )
    }

    // Calcula a data de inicio da view para os Data Headers e para o Header Datepicker (pode derivar da memoization ISO também, ou simplesmente ser recomputada na renderizacao)
    const weekStartObj = view === 'week' ? getStartOfWeek(referenceDate) : referenceDate;

    return (
        <React.Fragment>
            {/* Controles da Grade */}
            <Card className="mb-3">
                <CardBody className="pb-2 d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                        <GradeHeader
                            referenceDate={referenceDate}
                            onReferenceDateChange={setReferenceDate}
                            view={view}
                            onViewChange={setView}
                            turn={turn}
                            onTurnChange={setTurn}
                            refresh={refresh}
                            isEmbed={isEmbed}
                            onCancelEmbed={handleCancel}
                        />
                    </div>
                    <div className="d-flex gap-2 ms-4">
                        <Button color="secondary" onClick={handleCancel} disabled={enrolling}>
                            <i className="mdi mdi-close me-1"></i> Cancelar
                        </Button>
                        <Button
                            color={mode === 'trial' ? 'warning' : 'success'}
                            onClick={handleConfirm}
                            disabled={enrolling || (mode === 'trial' ? !selectedSession : selectedClasses.length === 0)}
                            className="px-4"
                        >
                            {enrolling ? "Processando..." : (
                                <><i className={`mdi mdi-${mode === 'trial' ? 'star' : 'check'} me-1`}></i>
                                    {mode === 'trial' ? 'Agendar Experimental' : 'Confirmar Matrícula'}</>
                            )}
                        </Button>
                    </div>
                </CardBody>
            </Card>

            {/* Grade de Seleção */}
            <Card className="position-relative">
                <CardBody>
                    <NeoLoader type="overlay" loading={enrolling} label="Processando matrícula..." />
                    {!loadingData && sessions.length === 0 && (
                        <div className="alert alert-warning mb-3">
                            <i className="mdi mdi-alert me-2"></i>
                            <strong>Nenhuma sessão encontrada.</strong>
                            <p className="mb-0 mt-2">Não há sessões disponíveis para seleção neste período.</p>
                        </div>
                    )}
                    <GradeGrid
                        turn={turn}
                        view={view}
                        referenceDate={referenceDate}
                        weekStart={weekStartObj}
                        schedules={sessions}
                        loading={loadingData}
                        onSelectSchedule={handleSelectSchedule}
                        selectedScheduleId={null}
                        selectedScheduleKey={null}
                        mode="selection"
                        selectedClasses={selectedClasses}
                        selectedSession={selectedSession}
                        isClassSelected={isClassSelected}
                        isClassEnrolled={isClassEnrolled}
                    />
                </CardBody>
            </Card>
        </React.Fragment>
    )
}

export default EnrollmentGrade